import React from 'react';
import { Clock, AlertCircle, Shield, MessageSquare, TrendingDown, Zap } from 'lucide-react';
import { Pattern } from '../../types/scan';
import { formatMessagesAnalyzed } from '../../utils/scanReport';
interface ShareableResultCardProps {
  score: number;
  riskLevel: 'low' | 'medium' | 'high';
  /** Real message count; null renders "Conversation analyzed". */
  messageCount?: number | null;
  topPatterns?: Pattern[];
  variant?: 'square' | 'vertical';
  summary?: string;
  theme?: 'dark' | 'light';
}
export function ShareableResultCard({
  score,
  riskLevel = 'low',
  messageCount = null,
  topPatterns = [],
  variant = 'square',
  summary,
  theme: cardTheme = 'dark'
}: ShareableResultCardProps) {
  const isVertical = variant === 'vertical';
  const isLight = cardTheme === 'light';
  // Theme Configuration based on risk level
  const themeConfig = {
    high: {
      color: '#EF4444',
      glowColor: 'rgba(239, 68, 68, 0.5)',
      textClass: 'text-red-500',
      bgClass: 'bg-red-500/10',
      borderClass: 'border-red-500/20',
      label: 'High Risk',
      verdict: 'TOXIC',
      summary: 'This conversation shows highly toxic communication patterns.',
      lightBadgeBg: '#FEF2F2',
      lightBadgeText: '#DC2626',
      lightBadgeBorder: '#FCA5A5'
    },
    medium: {
      color: '#F59E0B',
      glowColor: 'rgba(245, 158, 11, 0.5)',
      textClass: 'text-amber-500',
      bgClass: 'bg-amber-500/10',
      borderClass: 'border-amber-500/20',
      label: 'Medium Risk',
      verdict: 'CONCERNING',
      summary: 'This conversation shows some concerning communication patterns.',
      lightBadgeBg: '#FFFBEB',
      lightBadgeText: '#D97706',
      lightBadgeBorder: '#FCD34D'
    },
    low: {
      color: '#10B981',
      glowColor: 'rgba(16, 185, 129, 0.5)',
      textClass: 'text-emerald-500',
      bgClass: 'bg-emerald-500/10',
      borderClass: 'border-emerald-500/20',
      label: 'Low Risk',
      verdict: 'HEALTHY',
      summary: 'This conversation shows generally healthy communication patterns.',
      lightBadgeBg: '#ECFDF5',
      lightBadgeText: '#059669',
      lightBadgeBorder: '#6EE7B7'
    }
  };
  const theme = themeConfig[riskLevel] || themeConfig.low;
  // Helper to extract pattern info
  const getPatternInfo = (pattern: any) => {
    if (typeof pattern === 'string') {
      return {
        type: pattern,
        severity: 'MEDIUM' as const,
        description: ''
      };
    }
    return {
      type: pattern.type || 'Unknown',
      severity: pattern.severity || 'MEDIUM',
      description: pattern.description || ''
    };
  };
  // Helper functions
  const getPatternIcon = (type: string) => {
    if (!type) return Clock;
    const lower = type.toLowerCase();
    if (lower.includes('gaslight')) return AlertCircle;
    if (lower.includes('manipulat')) return Shield;
    if (lower.includes('dismiss')) return MessageSquare;
    if (lower.includes('blame')) return TrendingDown;
    if (lower.includes('aggress')) return Zap;
    return Clock;
  };
  const getSeverityColor = (severity: string) => {
    const s = severity?.toLowerCase();
    if (s === 'high') return '#EF4444';
    if (s === 'medium') return '#F59E0B';
    return '#10B981';
  };
  const getLightSeverityStyles = (severity: string) => {
    const s = severity?.toLowerCase();
    if (s === 'high') return {
      bg: '#FEF2F2',
      text: '#DC2626',
      border: '#FCA5A5'
    };
    if (s === 'medium') return {
      bg: '#FFFBEB',
      text: '#D97706',
      border: '#FCD34D'
    };
    return {
      bg: '#ECFDF5',
      text: '#059669',
      border: '#6EE7B7'
    };
  };
  const displayPatterns = topPatterns.slice(0, isVertical ? 4 : 3);
  const today = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const displaySummary = summary || theme.summary;
  // Common Background Grid
  const BackgroundGrid = () => <div className="absolute inset-0 pointer-events-none opacity-20" data-id="element-412" data-mp="37ypp9ikrkzxe">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)]" data-id="element-413" data-mp="3s25c7q3d24m3" />
    </div>;
  if (isVertical) {
    // --- VERTICAL (STORY) VARIANT ---
    const radius = 160;
    const strokeWidth = 28;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - score / 100 * circumference;
    const ringSize = 400;
    if (isLight) {
      return <div className="w-[1080px] h-[1920px] bg-[#FAFAFA] flex flex-col font-sans text-slate-900 relative overflow-hidden" data-id="element-414" data-mp="3i3plvpc5qzdn">
          {/* Header */}
          <header className="px-16 pt-20 pb-8 relative z-10" data-id="element-415" data-mp="xe8mnbyyowzu">
            <div className="flex items-center justify-between mb-6" data-id="element-416" data-mp="xdqebgz0ysbk">
              <h1 className="text-3xl font-black tracking-tight text-slate-900" data-id="element-417" data-mp="12cjvbjdit1gh">
                ToxicDetector.ai
              </h1>
              <span className="text-xl text-slate-500 font-medium" data-id="element-418" data-mp="2mum0thv2ji5x">
                Chat Analysis Result
              </span>
            </div>
            <div className="w-full h-px bg-slate-200" data-id="element-419" data-mp="2d5awe8j4n1od" />
          </header>

          {/* Main Content */}
          <main className="flex-1 flex flex-col items-center px-16 pt-12 relative z-10" data-id="element-420" data-mp="2870xpny71iy0">
            {/* Score Ring */}
            <div className="relative flex items-center justify-center mb-12" style={{
            width: ringSize,
            height: ringSize
          }} data-id="element-421" data-mp="245xhh0c8hkw9">
              <svg className="w-full h-full transform -rotate-90" viewBox={`0 0 ${ringSize} ${ringSize}`} data-id="element-422" data-mp="1u7htv7cf0tri">
                <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke="#F1F5F9" strokeWidth={strokeWidth} data-id="element-423" data-mp="3okhb14wg1w0w" />
                <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke={theme.color} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} data-id="element-424" data-mp="1huw8c3gf2gql" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-425" data-mp="20kvog0ua4lhv">
                <span className="text-[130px] font-black tracking-tighter leading-none text-slate-900" data-id="element-426" data-mp="25jywzv9gx9ul">
                  {score}%
                </span>
              </div>
            </div>

            {/* Verdict */}
            <div className="flex flex-col items-center mb-12" data-id="element-427" data-mp="g74v0eeiw1me">
              <h2 className="text-7xl font-black tracking-tight mb-6 uppercase text-center text-slate-900" data-id="element-428" data-mp="g5k20t7uwqqn">
                {score}% {theme.verdict}
              </h2>
              <div className="px-6 py-2 rounded-full border flex items-center gap-2" style={{
              backgroundColor: theme.lightBadgeBg,
              borderColor: theme.lightBadgeBorder
            }} data-id="element-429" data-mp="coeemijnbf14">
                <div className="w-2.5 h-2.5 rounded-full" style={{
                backgroundColor: theme.color
              }} data-id="element-430" data-mp="hoaj0iw998h7" />
                <span className="text-xl font-bold uppercase tracking-wide" style={{
                color: theme.lightBadgeText
              }} data-id="element-431" data-mp="3l50v9fifkhrc">
                  {theme.label} Detected
                </span>
              </div>
            </div>

            {/* Summary */}
            <div className="text-center mb-4 max-w-3xl" data-id="element-432" data-mp="37ct4q5hqpn13">
              <p className="text-3xl font-semibold text-slate-800 leading-relaxed" data-id="element-433" data-mp="37b9nabkcl5xq">
                {displaySummary}
              </p>
            </div>

            <div className="text-center mb-16" data-id="element-434" data-mp="2gwhxb3yw4747">
              <p className="text-xl text-slate-500 font-medium" data-id="element-435" data-mp="2lu9plz3g5v8x">
                Based on communication patterns, not intent.
              </p>
            </div>

            {/* Patterns List */}
            <div className="w-full max-w-4xl flex flex-col gap-6 mb-16" data-id="element-436" data-mp="2qvql1f35wpi4">
              {displayPatterns.map((pattern, index) => {
              const info = getPatternInfo(pattern);
              const PatternIcon = getPatternIcon(info.type);
              const lightStyles = getLightSeverityStyles(info.severity);
              return <div key={index} className="bg-white rounded-3xl border border-slate-200 p-8 flex items-center justify-between shadow-sm" data-id="element-437" data-mp="39kx26xob591t" data-mpi={index}>
                    <div className="flex items-center gap-5" data-id="element-438" data-mp="3ilv4nbj3l8ia">
                      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-600" data-id="element-439" data-mp="3njdfj7godxsx">
                        <PatternIcon size={24} strokeWidth={2} data-id="element-440" data-mp="1bxcne2hmj3gi" />
                      </div>
                      <h3 className="text-3xl font-bold text-slate-900 capitalize" data-id="element-441" data-mp="16wxatd561zzl">
                        {info.type}
                      </h3>
                    </div>
                    <span className="px-5 py-2 rounded-full text-sm font-bold uppercase tracking-widest border" style={{
                  backgroundColor: lightStyles.bg,
                  color: lightStyles.text,
                  borderColor: lightStyles.border
                }} data-id="element-442" data-mp="xwifdkyhn4id">
                      {info.severity}
                    </span>
                  </div>;
            })}

              {displayPatterns.length === 0 && <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center shadow-sm" data-id="element-443" data-mp="2ebjwzj8uuewb">
                  <p className="text-2xl font-medium text-slate-500" data-id="element-444" data-mp="29ih31a0613fl">
                    No concerning patterns detected.
                  </p>
                </div>}
            </div>

            {/* Stats Row */}
            <div className="flex items-center justify-center gap-12 text-2xl font-bold text-slate-900 mb-12" data-id="element-445" data-mp="28s7n0ojvm8yg">
              <div className="flex flex-col items-start" data-id="element-446" data-mp="2io2d24qfj6b9">
                <span className="text-lg font-bold text-slate-900 mb-1" data-id="element-447" data-mp="2imhi4e07dlcv">
                  Messages:
                </span>
                <span className="text-xl" data-id="element-448" data-mp="2wlh4tg45nq6e">{messageCount || '—'}</span>
              </div>
              <div className="w-px h-12 bg-slate-200" data-id="element-449" data-mp="2rmmocktnudpg" />
              <div className="flex flex-col items-start" data-id="element-450" data-mp="36dwnygr6uksv">
                <span className="text-lg font-bold text-slate-900 mb-1" data-id="element-451" data-mp="36fg3g550srtl">
                  Patterns Found: {topPatterns.length}
                </span>
              </div>
              <div className="w-px h-12 bg-slate-200" data-id="element-452" data-mp="3feuw9b8nteh9" />
              <div className="flex flex-col items-start" data-id="element-453" data-mp="3afqjybqfv0mp">
                <span className="text-lg font-bold text-slate-900 mb-1" data-id="element-454" data-mp="3ad5hplm8aiof">
                  Risk Level:
                </span>
                <span className="text-xl capitalize" data-id="element-455" data-mp="2uh0a9g1iz7m">{riskLevel}</span>
              </div>
            </div>

            <div className="text-center mb-auto" data-id="element-456" data-mp="3l0vca2ur0z3d">
              <p className="text-2xl font-medium text-slate-700" data-id="element-457" data-mp="3l02e3q9uvhwu">
                Scan your conversations for unhealthy patterns
              </p>
            </div>

            {/* Brand Footer */}
            <div className="w-full max-w-4xl bg-[#F8FAFC] rounded-3xl p-10 flex flex-col items-center justify-center text-center mt-12 mb-16" data-id="element-458" data-mp="3b97pwgd1u88r">
              <div className="flex items-center gap-4 mb-4" data-id="element-459" data-mp="31cml9w4xyz5g">
                <span className="text-4xl" data-id="element-460" data-mp="31a9t6q5gz8eo">🚩</span>
                <span className="text-4xl font-black text-slate-900 tracking-tight" data-id="element-461" data-mp="36ao4ch2daygx">
                  Made with ToxicDetector.ai
                </span>
              </div>
              <span className="text-2xl text-slate-500 font-medium" data-id="element-462" data-mp="2xarisob9yfq3">
                AI-powered relationship analysis
              </span>
            </div>
          </main>
        </div>;
    }
    return <div className="w-[1080px] h-[1920px] bg-[#0A0A0A] flex flex-col font-sans text-white relative overflow-hidden" style={{
      '--theme-glow-color': theme.glowColor
    } as React.CSSProperties} data-id="element-463" data-mp="y42ozym4fwxa">
        <BackgroundGrid data-id="element-464" data-mp="p4z54sf4rdhq" />

        {/* Subtle glow behind ring */}
        <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full blur-[120px] opacity-20 pointer-events-none" style={{
        backgroundColor: theme.color
      }} data-id="element-465" data-mp="1lsc82srya6kq" />

        {/* Header */}
        <header className="px-16 pt-20 pb-8 relative z-10" data-id="element-466" data-mp="17tw0z0q6ee22">
          <div className="flex items-center justify-between mb-6" data-id="element-467" data-mp="17tciniwm5nni">
            <div className="flex items-center gap-4" data-id="element-468" data-mp="1elaqv92p2jz">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-black font-black text-xl tracking-tighter" data-id="element-469" data-mp="1b7b3ursn7g8">
                TD
              </div>
              <h1 className="text-3xl font-black tracking-tight text-white" data-id="element-470" data-mp="27zpp8uxz0wds">
                ToxicDetector.ai
              </h1>
            </div>
            <span className="text-xl text-white/50 font-mono uppercase tracking-widest" data-id="element-471" data-mp="2h1yylhhi6jpl">
              Forensic Report
            </span>
          </div>
          <div className="w-full h-px bg-white/10" data-id="element-472" data-mp="2lvbmfpecymha" />
        </header>

        {/* Main Content */}
        <main className="flex-1 flex flex-col items-center px-16 pt-12 relative z-10" data-id="element-473" data-mp="2quehch0mjcbj">
          {/* Score Ring */}
          <div className="relative flex items-center justify-center mb-12" style={{
          width: ringSize,
          height: ringSize
        }} data-id="element-474" data-mp="2vt7ta509iv9l">
            <svg className="w-full h-full transform -rotate-90" viewBox={`0 0 ${ringSize} ${ringSize}`} style={{
            filter: 'drop-shadow(0 0 20px var(--theme-glow-color))'
          }} data-id="element-475" data-mp="34pqk9boxo93j">
              <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={strokeWidth} data-id="element-476" data-mp="f2aor3rjs7dl" />
              <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke={theme.color} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} data-id="element-477" data-mp="k3865e4qgxe1" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-478" data-mp="1cgx6donr906">
              <span className="text-[130px] font-black tracking-tighter leading-none" style={{
              color: theme.color
            }} data-id="element-479" data-mp="1anuf9h618xg">
                {score}
              </span>
              <span className="text-2xl font-mono text-white/50 mt-2 uppercase tracking-widest" data-id="element-480" data-mp="1ka3sv7c9rf35">
                Score
              </span>
            </div>
          </div>

          {/* Verdict */}
          <div className="flex flex-col items-center mb-16" data-id="element-481" data-mp="1bbi1qnsqerqr">
            <h2 className="text-7xl font-black tracking-tight mb-4 uppercase text-center" style={{
            color: theme.color
          }} data-id="element-482" data-mp="1fc22e1cikdhn">
              {theme.verdict}
            </h2>
            <div className={`px-6 py-2 rounded-full border ${theme.borderClass} ${theme.bgClass}`} data-id="element-483" data-mp="27zg0i2ry7f3c">
              <span className={`text-xl font-bold ${theme.textClass} uppercase tracking-widest`} data-id="element-484" data-mp="1u0om0f96breo">
                {theme.label} Detected
              </span>
            </div>
          </div>

          {/* Summary */}
          <div className="text-center mb-16 max-w-3xl" data-id="element-485" data-mp="nlfgorn8im1n">
            <p className="text-3xl font-medium text-white/90 leading-relaxed" data-id="element-486" data-mp="nit7knkp3yhb">
              {displaySummary}
            </p>
          </div>

          {/* Patterns List */}
          <div className="w-full max-w-4xl flex flex-col gap-6 mb-16" data-id="element-487" data-mp="2kbifem3qyq5j">
            <h3 className="text-2xl font-mono text-white/50 uppercase tracking-widest mb-2 pl-4" data-id="element-488" data-mp="2fjgt07cv7j1s">
              Detected Patterns
            </h3>
            {displayPatterns.map((pattern, index) => {
            const info = getPatternInfo(pattern);
            const PatternIcon = getPatternIcon(info.type);
            const sevColor = getSeverityColor(info.severity);
            return <div key={index} className="bg-white/5 rounded-3xl border border-white/10 p-8 flex flex-col gap-4 relative overflow-hidden" data-id="element-489" data-mp="1wkmi59giv2qw" data-mpi={index}>
                  {/* Severity Accent Line */}
                  <div className="absolute left-0 top-0 bottom-0 w-2" style={{
                backgroundColor: sevColor
              }} data-id="element-490" data-mp="3qxb7ijw1aife" />

                  <div className="flex items-center justify-between pl-4" data-id="element-491" data-mp="18o88rdltdyc1">
                    <div className="flex items-center gap-5" data-id="element-492" data-mp="1dnb4u8jtgu7v">
                      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white" data-id="element-493" data-mp="1ifm55cigyi0y">
                        <PatternIcon size={24} strokeWidth={2} data-id="element-494" data-mp="1mg62oh6yfnnm" />
                      </div>
                      <h3 className="text-3xl font-bold text-white capitalize" data-id="element-495" data-mp="ps8chqlt08mn">
                        {info.type}
                      </h3>
                    </div>
                    <span className="px-5 py-2 rounded-full text-sm font-mono font-bold uppercase tracking-widest" style={{
                  '--sev-color': sevColor,
                  '--sev-color-20': `${sevColor}20`,
                  '--sev-color-40': `${sevColor}40`,
                  backgroundColor: 'var(--sev-color-20)',
                  color: 'var(--sev-color)',
                  border: '1px solid var(--sev-color-40)'
                } as React.CSSProperties} data-id="element-496" data-mp="ys50dq0dd3gn">
                      {info.severity}
                    </span>
                  </div>
                  {info.description && <p className="text-xl text-white/60 leading-relaxed pl-21 ml-[68px]" data-id="element-497" data-mp="1utbn694gsm1i">
                      {info.description}
                    </p>}
                </div>;
          })}

            {displayPatterns.length === 0 && <div className="bg-white/5 rounded-3xl border border-white/10 p-12 text-center" data-id="element-498" data-mp="2ijxx65y14ge5">
                <p className="text-2xl font-medium text-white/50" data-id="element-499" data-mp="2ejnim4w68hno">
                  No concerning patterns detected.
                </p>
              </div>}
          </div>

          {/* Stats Row */}
          <div className="flex items-center justify-center gap-12 text-2xl font-mono text-white/60 mb-auto" data-id="element-500" data-mp="k77dx8j0xbkx">
            <div className="flex flex-col items-center" data-id="element-501" data-mp="a3jxsw635jpr">
              <span className="text-white font-bold text-4xl mb-2" data-id="element-502" data-mp="e1hmzyzuw5t5">
                {messageCount || '—'}
              </span>
              <span className="text-sm uppercase tracking-widest" data-id="element-503" data-mp="9a4nc9bkhdz">
                Messages
              </span>
            </div>
            <div className="w-px h-16 bg-white/10" data-id="element-504" data-mp="3miukk6no3wd1" />
            <div className="flex flex-col items-center" data-id="element-505" data-mp="3mh1diq957a4i">
              <span className="text-white font-bold text-4xl mb-2" data-id="element-506" data-mp="3ri76mp6pszek">
                {topPatterns.length}
              </span>
              <span className="text-sm uppercase tracking-widest" data-id="element-507" data-mp="3dj7sibgtw2wn">
                Patterns
              </span>
            </div>
          </div>

          {/* Brand Footer */}
          <div className="w-full max-w-4xl bg-white/5 border border-white/10 rounded-3xl p-10 flex flex-col items-center justify-center text-center mt-16 mb-16 relative overflow-hidden" data-id="element-508" data-mp="1bq9v02c3wm5d">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-50" data-id="element-509" data-mp="2ka2d6l6g6ycs" />
            <div className="flex items-center gap-4 mb-4" data-id="element-510" data-mp="2p1appq32qzu6">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-black font-black text-lg tracking-tighter" data-id="element-511" data-mp="2abwgchzi0ies">
                TD
              </div>
              <span className="text-3xl font-black text-white tracking-tight" data-id="element-512" data-mp="2f8d6tiu678v1">
                ToxicDetector.ai
              </span>
            </div>
            <span className="text-xl text-white/50 font-mono uppercase tracking-widest" data-id="element-513" data-mp="37y25vqnbkyu0">
              AI-Powered Relationship Analysis
            </span>
          </div>
        </main>
      </div>;
  }
  // --- SQUARE (1:1) VARIANT ---
  if (isLight) {
    const radius = 120;
    const strokeWidth = 20;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - score / 100 * circumference;
    const ringSize = 300;
    return <div className="w-[1080px] h-[1080px] bg-[#FAFAFA] flex flex-col font-sans text-slate-900 relative overflow-hidden p-16" data-id="element-514" data-mp="1f29ckrkxq8rz">
        {/* Header */}
        <header className="flex items-center justify-between mb-12 relative z-10" data-id="element-515" data-mp="1a36h9yurbgx7">
          <h1 className="text-4xl font-black tracking-tight text-slate-900" data-id="element-516" data-mp="1oze291dzomdj">
            ToxicDetector.ai
          </h1>
          <div className="text-right" data-id="element-517" data-mp="1k2dr4c2a8tfk">
            <div className="text-slate-500 font-medium text-xl" data-id="element-518" data-mp="wbruri1n4xle">
              Chat Analysis Result
            </div>
            <div className="text-slate-400 font-medium text-base mt-1" data-id="element-519" data-mp="rdq52e9pm00y">
              {today}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex flex-col relative z-10" data-id="element-520" data-mp="11beapz8usrxw">
          <div className="flex items-center justify-between mb-16" data-id="element-521" data-mp="119thccy9zf1m">
            <div className="flex flex-col" data-id="element-522" data-mp="dkicaj9w6yho">
              <h2 className="text-3xl font-medium text-slate-500 mb-4" data-id="element-523" data-mp="dj784icjboq1">
                Analysis Result
              </h2>
              <span className="text-7xl font-black tracking-tight uppercase text-slate-900" data-id="element-524" data-mp="yauk2jb2xct2">
                {score}% {theme.verdict}
              </span>
              <div className="mt-6 flex items-center gap-4" data-id="element-525" data-mp="12ad6xkmgnxyj">
                <div className="px-6 py-2 rounded-full border flex items-center gap-2" style={{
                backgroundColor: theme.lightBadgeBg,
                borderColor: theme.lightBadgeBorder
              }} data-id="element-526" data-mp="172mtqrsa4hwl">
                  <div className="w-2.5 h-2.5 rounded-full" style={{
                  backgroundColor: theme.color
                }} data-id="element-527" data-mp="jcsg4804ypba" />
                  <span className="text-xl font-bold uppercase tracking-wide" style={{
                  color: theme.lightBadgeText
                }} data-id="element-528" data-mp="tc19ayn6fzwl">
                    {theme.label}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-end" data-id="element-529" data-mp="3p863uuwjo97o">
              <div className="relative flex items-center justify-center" style={{
              width: ringSize,
              height: ringSize
            }} data-id="element-530" data-mp="3k92wh3t3dqwj">
                <svg className="w-full h-full transform -rotate-90" viewBox={`0 0 ${ringSize} ${ringSize}`} data-id="element-531" data-mp="3airtbjh8flqp">
                  <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke="#F1F5F9" strokeWidth={strokeWidth} data-id="element-532" data-mp="31j4qct8uu7vj" />
                  <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke={theme.color} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} data-id="element-533" data-mp="2rmrw9u9eds09" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-534" data-mp="nwlub63834bq">
                  <span className="text-[80px] font-black tracking-tighter leading-none text-slate-900" data-id="element-535" data-mp="sux63md2vowl">
                    {score}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Patterns Box */}
          <div className="w-full flex-1 flex flex-col" data-id="element-536" data-mp="jvtnsklqjdj5">
            <h3 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-537" data-mp="56dxt6u13c4i">
              Detected Patterns ({topPatterns.length})
            </h3>

            <div className="grid grid-cols-1 gap-4" data-id="element-538" data-mp="a4z0re0o09wn">
              {displayPatterns.map((pattern, index) => {
              const info = getPatternInfo(pattern);
              const lightStyles = getLightSeverityStyles(info.severity);
              return <div key={index} className="flex items-center justify-between bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-id="element-539" data-mp="3domx110wkena" data-mpi={index}>
                    <span className="text-3xl font-bold text-slate-900 capitalize pl-4" data-id="element-540" data-mp="3mpctm6xzm922">
                      {info.type}
                    </span>
                    <span className="px-5 py-2 rounded-full text-lg font-bold uppercase tracking-widest border" style={{
                  backgroundColor: lightStyles.bg,
                  color: lightStyles.text,
                  borderColor: lightStyles.border
                }} data-id="element-541" data-mp="h3er4lp9nqf">
                      {info.severity}
                    </span>
                  </div>;
            })}

              {displayPatterns.length === 0 && <div className="flex items-center justify-center bg-white border border-slate-200 rounded-2xl p-10 shadow-sm" data-id="element-542" data-mp="e9jd3s3noxl0">
                  <span className="text-2xl font-medium text-slate-500" data-id="element-543" data-mp="382atz9052pn2">
                    No concerning patterns detected
                  </span>
                </div>}
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-slate-200 flex items-center justify-between relative z-10" data-id="element-544" data-mp="3ls4i1p3tl5vq">
          <div className="flex items-center gap-6 text-2xl font-medium text-slate-500" data-id="element-545" data-mp="3qq61wc92l989">
            <span data-id="element-546" data-mp="3vozgy94098aj">{formatMessagesAnalyzed(messageCount)}</span>
            <span className="text-slate-300" data-id="element-547" data-mp="3m8avgpuasji">·</span>
            <span data-id="element-548" data-mp="3njcpazq5q7z">{today}</span>
          </div>
          <div className="flex items-center gap-3" data-id="element-549" data-mp="8lci41t7eg77">
            <span className="text-3xl" data-id="element-550" data-mp="8kt1cofahxur">🚩</span>
            <span className="text-2xl font-bold text-slate-900 tracking-wide" data-id="element-551" data-mp="25dmkpe6ksvyz">
              toxicdetector.ai
            </span>
          </div>
        </footer>
      </div>;
  }
  return <div className="w-[1080px] h-[1080px] bg-[#0A0A0A] flex flex-col font-sans text-white relative overflow-hidden p-16" data-id="element-552" data-mp="22t6mh6pvwc6x">
      <BackgroundGrid data-id="element-553" data-mp="1y1yb454zugr4" />

      {/* Subtle glow behind score */}
      <div className="absolute top-[30%] left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-[150px] opacity-15 pointer-events-none" style={{
      backgroundColor: theme.color
    }} data-id="element-554" data-mp="1y0dl8stmjlzl" />

      {/* Header */}
      <header className="flex items-center justify-between mb-12 relative z-10" data-id="element-555" data-mp="2lqzpsa4f6o4u">
        <div className="flex items-center gap-4" data-id="element-556" data-mp="2gsg48atqned0">
          <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center text-black font-black text-2xl tracking-tighter" data-id="element-557" data-mp="2gtr3pyg5hzyb">
            TD
          </div>
          <h1 className="text-4xl font-black tracking-tight text-white" data-id="element-558" data-mp="27r87hx6vell1">
            ToxicDetector.ai
          </h1>
        </div>
        <div className="text-right" data-id="element-559" data-mp="10g0xal1k6zli">
          <div className="text-white/50 font-mono text-lg uppercase tracking-widest mb-1" data-id="element-560" data-mp="wdcouegrbup2">
            Date
          </div>
          <div className="text-white font-medium text-xl" data-id="element-561" data-mp="2vgtaopd1m1to">{today}</div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col relative z-10" data-id="element-562" data-mp="30iilc3wcsdh8">
        <div className="flex items-center justify-between mb-16" data-id="element-563" data-mp="2mhy4vedwutqw">
          <div className="flex flex-col" data-id="element-564" data-mp="2mjiwow8ubyl2">
            <h2 className="text-3xl font-mono text-white/50 uppercase tracking-widest mb-4" data-id="element-565" data-mp="2rharby0vdysy">
              Forensic Analysis
            </h2>
            <span className="text-7xl font-black tracking-tight uppercase" style={{
            color: theme.color
          }} data-id="element-566" data-mp="3f7yaxgk5ecvc">
              {theme.verdict}
            </span>
            <div className="mt-4 flex items-center gap-4" data-id="element-567" data-mp="3aevixcv0rbh9">
              <span className="text-2xl text-white/70 font-medium" data-id="element-568" data-mp="3adaodn8pfsje">
                Risk Level:
              </span>
              <div className={`px-4 py-1.5 rounded-full border ${theme.borderClass} ${theme.bgClass} inline-block`} data-id="element-569" data-mp="1xh3kw7cbuwj">
                <span className={`text-lg font-bold ${theme.textClass} uppercase tracking-widest`} data-id="element-570" data-mp="g3sl4v6nxren">
                  {theme.label}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end" data-id="element-571" data-mp="pwrhx0cbco9c">
            <span className="text-[180px] font-black leading-none tracking-tighter" style={{
            color: theme.color,
            textShadow: `0 0 40px ${theme.glowColor}`
          }} data-id="element-572" data-mp="kwlopfqeatp5">
              {score}
            </span>
            <span className="text-3xl font-mono text-white/50 uppercase tracking-widest mt-2" data-id="element-573" data-mp="74fkms0iod62">
              Toxicity Score
            </span>
          </div>
        </div>

        {/* Patterns Box */}
        <div className="w-full flex-1 flex flex-col" data-id="element-574" data-mp="1iijdtvza5xa2">
          <h3 className="text-2xl font-mono text-white/50 uppercase tracking-widest mb-6" data-id="element-575" data-mp="1ilf89abx63tr">
            Detected Patterns ({topPatterns.length})
          </h3>

          <div className="grid grid-cols-1 gap-4" data-id="element-576" data-mp="32lzi6mclc9p6">
            {displayPatterns.map((pattern, index) => {
            const info = getPatternInfo(pattern);
            const sevColor = getSeverityColor(info.severity);
            return <div key={index} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-2xl p-6 relative overflow-hidden" data-id="element-577" data-mp="2tm2uamy0zev6" data-mpi={index}>
                  <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{
                backgroundColor: sevColor
              }} data-id="element-578" data-mp="3h9sy685l9e8a" />
                  <span className="text-3xl font-bold text-white capitalize pl-4" data-id="element-579" data-mp="37kj26h7mjbew">
                    {info.type}
                  </span>
                  <span className="px-5 py-2 rounded-full text-lg font-mono font-bold uppercase tracking-widest" style={{
                '--sev-color': sevColor,
                '--sev-color-20': `${sevColor}20`,
                '--sev-color-40': `${sevColor}40`,
                backgroundColor: 'var(--sev-color-20)',
                color: 'var(--sev-color)',
                border: '1px solid var(--sev-color-40)'
              } as React.CSSProperties} data-id="element-580" data-mp="8vk9hb66t7le">
                    {info.severity}
                  </span>
                </div>;
          })}

            {displayPatterns.length === 0 && <div className="flex items-center justify-center bg-white/5 border border-white/10 rounded-2xl p-10" data-id="element-581" data-mp="1445ra2275abp">
                <span className="text-2xl font-medium text-white/50" data-id="element-582" data-mp="191o1dvry9vla">
                  No concerning patterns detected
                </span>
              </div>}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 pt-8 border-t border-white/10 flex items-center justify-between relative z-10" data-id="element-583" data-mp="3e60cancx385c">
        <div className="flex items-center gap-6 text-2xl font-mono text-white/50" data-id="element-584" data-mp="3a4oxlcvl07bx">
          <span data-id="element-585" data-mp="3a2lym3cgb3v2">{formatMessagesAnalyzed(messageCount)}</span>
        </div>
        <div className="flex items-center gap-3" data-id="element-586" data-mp="3nx4stri8r12a">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" data-id="element-587" data-mp="3j4ldaozztvuu" />
          <span className="text-2xl font-bold text-white tracking-wide" data-id="element-588" data-mp="1olod2otmalh">
            toxicdetector.ai
          </span>
        </div>
      </footer>
    </div>;
}